import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useStore from "../../../../useStore";
import Loader from "../../../Elements/Loader/Loader";
import Button from "../../../Elements/Button/Button";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import CustomCalendar from "../../../Elements/Calendar/Calendar";
import ReporteSemanal from "../ReporteSemanal/ReporteSemanal";
import "./PedidosRestaurante.css";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import { fmtDate } from "../../../../Utilities/Fecha/fecha";
import { badgeClassByPaymentStatus, badgeClassByStatus } from "../../../../Utilities/Status/statusColor";
import { isMpPending } from "../../../../Utilities/Payment/MpPending";
import { actionsByStatus } from "../../../../Utilities/Status/actionStatus";

const actionMsg = {
  preparar: "Pedido movido a PREPARANDO",
  enviar: "Pedido movido a ENVIADO",
  cancelar: "Pedido CANCELADO",
};

export default function PedidosRestaurante() {
  const { restauranteId } = useParams();
  const showToast = useToast();

  const {
    ordersByRestaurant,
    loadingGetOrdersByRestaurant,
    errorGetOrdersByRestaurant,
    nextTokenOrdersByRestaurant,
    fetchDataGetOrdersByRestaurant,
    changeStatus,
  } = useStore();

  const [updating, setUpdating] = useState({});
  const [selectedAction, setSelectedAction] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());

/*   const orderDates = useMemo(() => {
    const set = new Set();
    if (ordersByRestaurant) {
      ordersByRestaurant.forEach((o) => {
        if (o.createdAt) {
          const d = new Date(o.createdAt);
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          set.add(key);
        }
      });
    }
    return set;
  }, [ordersByRestaurant]); */

  const filteredOrders = useMemo(() => {
    if (!ordersByRestaurant) return [];
    return ordersByRestaurant.filter((o) => {
      if (!o.createdAt) return false;
      const d = new Date(o.createdAt);
      return (
        d.getFullYear() === selectedDate.getFullYear() &&
        d.getMonth() === selectedDate.getMonth() &&
        d.getDate() === selectedDate.getDate()
      );
    });
  }, [ordersByRestaurant, selectedDate]);

  // 👇 Reemplaza tu orderDates por dos sets: "todas" y "pendientes"
  const orderDatesAll = useMemo(() => {
    const set = new Set();
    (ordersByRestaurant || []).forEach((o) => {
      if (!o?.createdAt) return;
      const d = new Date(o.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      set.add(key);
    });
    return set;
  }, [ordersByRestaurant]);

  const pendingDates = useMemo(() => {
    const set = new Set();
    (ordersByRestaurant || []).forEach((o) => {
      const s = String(o?.status ?? "").trim().toLowerCase();
      if (s === "pendiente" || s === "preparando") {
        if (!o?.createdAt) return;
        const d = new Date(o.createdAt);
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        set.add(key);
      }
    });
    return set;
  }, [ordersByRestaurant]);

  useEffect(() => {
    if (!restauranteId) return;
    fetchDataGetOrdersByRestaurant(restauranteId, { limit: 25, append: false });
  }, [restauranteId, fetchDataGetOrdersByRestaurant]);

  useEffect(() => {
    if (errorGetOrdersByRestaurant) {
      showToast("error", errorGetOrdersByRestaurant);
    }
  }, [errorGetOrdersByRestaurant, showToast]);

  useEffect(() => {
    setSelectedAction((prev) => {
      if (!ordersByRestaurant || ordersByRestaurant.length === 0) return prev;

      const next = { ...prev };
      let changed = false;

      for (const ord of ordersByRestaurant) {
        const ops = actionsByStatus(ord.status);
        if (ops.length === 0 && next[ord.orderId]) {
          next[ord.orderId] = "";
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [ordersByRestaurant]);

  const cargarMas = () => {
    if (!nextTokenOrdersByRestaurant || !restauranteId) return;
    fetchDataGetOrdersByRestaurant(restauranteId, {
      limit: 25,
      nextToken: nextTokenOrdersByRestaurant,
      append: true,
    });
  };

  const handleChangeAction = (orderId, value) => {
    setSelectedAction((prev) => ({ ...prev, [orderId]: value }));
  };

  const handleApplyAction = async (orderId, action) => {
    if (!action) return;

    try {
      setUpdating((p) => ({ ...p, [orderId]: true }));
      const res = await changeStatus(restauranteId, orderId, action);

      if (res?.statusCode === 200) {
        setSelectedAction((p) => ({ ...p, [orderId]: "" }));
        const msg = res?.message || actionMsg[action] || `Pedido ${orderId} actualizado`;
        showToast("success", msg);

        await fetchDataGetOrdersByRestaurant(restauranteId, { limit: 25, append: false });
      } else {
        const msg = res?.message || res?.body?.message || "No se pudo actualizar el estado.";
        showToast("error", msg);
      }
    } catch (e) {
      console.error(e);
      showToast("error", e?.message || "Error al cambiar el estado.");
    } finally {
      setUpdating((p) => ({ ...p, [orderId]: false }));
    }
  };

  return (
    <div className="container-fluid container-mh pr-page">
      <div className="row mb-4">
        <div className="col">
          <h2 className="pr-title">📦 Pedidos del comercio</h2>
          <div className="mb-3">
            <CustomCalendar
              value={selectedDate}
              onChange={setSelectedDate}
              orderDates={orderDatesAll}      // días con cualquier orden (verde)
              pendingDates={pendingDates}      // días con pendientes/preparando (rojo)
            />
          </div>
          <p className="pr-subtext">
            Comercio ID: <code>{restauranteId}</code>
          </p>
        </div>
      </div>

      <ReporteSemanal restauranteId={restauranteId} selectedDate={selectedDate} />

      {loadingGetOrdersByRestaurant && <Loader />}

      {!!errorGetOrdersByRestaurant && (
        <div className="alert alert-danger">{errorGetOrdersByRestaurant}</div>
      )}

      {!loadingGetOrdersByRestaurant && !errorGetOrdersByRestaurant && (
        <>
          {(!filteredOrders || filteredOrders.length === 0) ? (
            <div className="alert alert-warning text-center">⚠️ No hay pedidos todavía.</div>
          ) : (
            <div className="row g-3">
              {filteredOrders.map((o) => {
                const tieneRepartidor = !!(o.repartidorAsignado && String(o.repartidorAsignado).trim());
                const isUpdating = !!updating[o.orderId];
                if (o.status === "preparando" && !tieneRepartidor) {
                  return (
                    <div key={o.orderId} className="col-12 col-md-6 col-lg-4">
                      <div className="card pr-card h-100 shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <h5 className="card-title">Pedido #{o.orderId}</h5>
                          <h6 className="text-muted">📍 {o.restauranteNombre || "Desconocido"}</h6>
                          <p className="text-muted">{fmtDate(o.createdAt)}</p>

                          <p className="mb-1">
                            Método: <strong>{o.paymentMethod || "-"}</strong>
                          </p>
                          <p className="mb-1">
                            Pago:{" "}
                            <span className={`badge bg-${o.paymentStatus === "paid" ? "success" : "warning"}`}>
                              {o.paymentStatus || "-"}
                            </span>
                          </p>

                          <p className="mb-3">
                            Estado orden:{" "}
                            <span className={`badge bg-${badgeClassByStatus(o.status)}`}>
                              {o.status}
                            </span>
                          </p>

                          <h6 className="text-end text-primary mb-3">
                            {fmtCurrency(o.totalAmount)}
                          </h6>

                          <p className="small text-muted mb-2">
                            Subtotal: {fmtCurrency(o.subtotal)} • Envío: {fmtCurrency(o.shippingCost)}{" "}
                            {o.tip ? <>• Propina: {fmtCurrency(o.tip)}</> : null}
                          </p>

                          {/* Aviso cuando falta repartidor */}
                          <div className="mt-2">
                            <label className="form-label small">Cambiar estado</label>
                            <div className="alert alert-warning py-2 px-3 mb-2">
                              No puedes <strong>ENVIAR</strong> el pedido sin un repartidor asignado.
                              <br />Espera a que un conductor tome el pedido.
                            </div>
                            <small className="text-muted d-block mt-1">
                              Transición requerida: <em>preparando → enviado</em> (requiere repartidor).
                            </small>
                          </div>

                          <div className="mt-auto">
                            <h6 className="pr-section-title mt-3">👤 Cliente</h6>
                            <p className="mb-0">{o?.customer?.name || "-"}</p>
                            <small className="text-muted">{o?.customer?.email || "-"}</small><br />
                            <small className="text-muted">{o?.customer?.phone || "-"}</small>

                            <h6 className="pr-section-title mt-3">🛒 Artículos</h6>
                            <ul className="list-unstyled small">
                              {(() => {
                                let items = o.items;

                                // Si viene como string desde Dynamo, intenta parsear
                                if (typeof items === "string") {
                                  try {
                                    items = JSON.parse(items);
                                  } catch {
                                    items = [];
                                  }
                                }

                                // Si no es array, lo forzamos a []
                                if (!Array.isArray(items)) {
                                  items = [];
                                }

                                return items
                                  .filter((it) => it?.id !== "envio" && it?.id !== "propina")
                                  .map((it, idx) => (
                                    <li key={idx}>
                                      {it.quantity} × {it.title} — {fmtCurrency(it.unit_price)}
                                    </li>
                                  ));
                              })()}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                const rawOptions = actionsByStatus(o.status);
                const options = rawOptions.filter(opt => !(opt.value === "enviar" && !tieneRepartidor));
                const sel = selectedAction[o.orderId] || (options[0]?.value || "");

                return (
                  <div key={o.orderId} className="col-12 col-md-6 col-lg-4">
                    <div className="card pr-card h-100 shadow-sm">
                      <div className="card-body d-flex flex-column">
                        <h5 className="card-title">Pedido #{o.orderId}</h5>
                        <h6 className="text-muted">📍 {o.restauranteNombre || "Desconocido"}</h6>
                        <p className="text-muted">{fmtDate(o.createdAt)}</p>

                        <p className="mb-1">
                          Método: <strong>{o.paymentMethod || "-"}</strong>
                        </p>
                        <p className="mb-1">
                          Pago:{" "}
                          <span className={`badge bg-${badgeClassByPaymentStatus(o.paymentStatus)}`}>
                            {o.paymentStatus || "-"}
                          </span>
                        </p>

                        <p className="mb-3">
                          Estado orden:{" "}
                          <span className={`badge bg-${badgeClassByStatus(o.status)}`}>
                            {o.status}
                          </span>
                        </p>

                        <h6 className="text-end text-primary mb-3">
                          {fmtCurrency(o.totalAmount)}
                        </h6>

                        <p className="small text-muted mb-2">
                          Subtotal: {fmtCurrency(o.subtotal)} • Envío: {fmtCurrency(o.shippingCost)}{" "}
                          {o.tip ? <>• Propina: {fmtCurrency(o.tip)}</> : null}
                        </p>

                        {options.length > 0 ? (
                          <div className="mt-2">
                            <label className="form-label small">Cambiar estado</label>
                            <div className="d-flex gap-2">
                              <select
                                className="form-select form-select-sm"
                                value={sel}
                                onChange={(e) => handleChangeAction(o.orderId, e.target.value)}
                                disabled={isUpdating || isMpPending(o)}
                              >
                                {options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>

                              <Button
                                text={isUpdating ? "Aplicando..." : "Aplicar"}
                                action={() => handleApplyAction(o.orderId, sel)}
                                color="blue"
                                fill
                                disabled={
                                  isUpdating ||
                                  !sel ||
                                  (sel === "enviar" && !tieneRepartidor) ||
                                  isMpPending(o)   // 👈 FALTABA ESTE "||"
                                }
                              />
                            </div>

                            {sel === "enviar" && !tieneRepartidor && (
                              <small className="text-danger d-block mt-1">
                                Debes asignar un repartidor antes de enviar.
                              </small>
                            )}

                            <small className="text-muted d-block mt-1">
                              Transiciones válidas aquí: pendiente → preparando, preparando → enviado.
                              (La entrega la maneja el repartidor)
                            </small>
                          </div>
                        ) : (
                          <small className="text-muted mt-2 d-block">
                            {o.status === "enviado"
                              ? "Pedido ENVIADO 🚚 (a cargo del repartidor)"
                              : o.status === "cancelado"
                                ? "Pedido cancelado"
                                : o.status === "entregado"
                                  ? "Pedido entregado ✅"
                                  : null}
                          </small>
                        )}

                        <div className="mt-auto">
                          <h6 className="pr-section-title mt-3">👤 Cliente</h6>
                          <p className="mb-0">{o?.customer?.name || "-"}</p>
                          <small className="text-muted">{o?.customer?.email || "-"}</small><br />
                          <small className="text-muted">{o?.customer?.phone || "-"}</small>

                          <h6 className="pr-section-title mt-3">🛒 Artículos</h6>
                          <ul className="list-unstyled small">
                            {(() => {
                              let items = o.items;

                              // Si viene como string desde Dynamo, intenta parsear
                              if (typeof items === "string") {
                                try {
                                  items = JSON.parse(items);
                                } catch {
                                  items = [];
                                }
                              }

                              // Si no es array, lo forzamos a []
                              if (!Array.isArray(items)) {
                                items = [];
                              }

                              return items
                                .filter((it) => it?.id !== "envio" && it?.id !== "propina")
                                .map((it, idx) => (
                                  <li key={idx}>
                                    {it.quantity} × {it.title} — {fmtCurrency(it.unit_price)}
                                  </li>
                                ));
                            })()}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {nextTokenOrdersByRestaurant && (
            <div className="row mt-4">
              <div className="col text-center">
                <Button text="Cargar más" action={cargarMas} color="blue" fill />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}